import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { Alert, Table, Badge, Button, Card, Spinner, Modal } from 'react-bootstrap';
import { fetchAllTimetable, updateTimetable } from '../redux/timetableSlice';
import { fetchTeachers } from '../../teachers/redux/teacherSlice';
import ReactToPrint from 'react-to-print';
import html2pdf from 'html2pdf.js';
import { Download, Printer } from "lucide-react";

// --- Draggable Component ---
const Draggable = ({ id, children, isDragging }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: id,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 9999 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{
        padding: '8px',
        border: '1px dashed #ccc',
        background: '#f8f9fa',
        cursor: 'grab',
        ...style
      }}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
};

// --- Droppable Component ---
const Droppable = ({ id, children }) => {
  const { setNodeRef } = useDroppable({
    id: id,
  });

  return (
    <td key={id} ref={setNodeRef}>
      {children}
    </td>
  );
};

const TimeTableView = ({ periods = [], timetableData = [], sectionId, sectionTimetable, sectionRefs, workingDays }) => {
  const [editMode, setEditMode] = useState(false);
  const [localPeriodsData, setLocalPeriodsData] = useState([]);
  const initialTimetableDataRef = useRef([]);
  const [activeId, setActiveId] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const teachers = useSelector((state) => state.teachers.teachers);

  const {
    loading,
    timetable: allTimetablesData, // Renamed from 'allTimetablesData' to 'timetable' in slice
  } = useSelector((state) => state.timetable);

  const { user } = useSelector(state => state.authSlice);
  const schoolId = useSelector((state) => state.masterData.school_id);
  const dispatch = useDispatch();

  const safePeriods = Array.isArray(periods) ? periods : [];
  const safeTeachers = Array.isArray(teachers) ? teachers : [];
  const safeAllTimetables = Array.isArray(allTimetablesData) ? allTimetablesData : [];

  console.log("Teachers Data:", safeTeachers);
  console.log("teacher max_periods_per_Days", safeTeachers[0]?.max_periods_per_day);

  useEffect(() => {
    setLocalPeriodsData([...timetableData]);
    initialTimetableDataRef.current = [...timetableData];
  }, [timetableData]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].slice(0, workingDays);

  const sensors = useSensors(useSensor(PointerSensor));
  const sortedPeriods = [...safePeriods].sort((a, b) => a.sequence_order - b.sequence_order);
  const [blurredBlocks, setBlurredBlocks] = useState([]);
  const printRef = useRef();
  const pdfRef = useRef();
  const [showPrintModal, setShowPrintModal] = useState(false);

  // --- Generate random blurred blocks for trial users ---
  useEffect(() => {
    if (user?.roles === "trial") {
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        .slice(0, workingDays);

      const blocks = [];

      days.forEach((day) => {
        // get all valid periods for the day
        const validPeriods = safePeriods.filter((p) => p.period_type === "period");

        // shuffle and take 4 (or less if not enough)
        const shuffled = [...validPeriods].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 4);

        selected.forEach((p) => {
          blocks.push(`${day}|${p.period_id}`);
        });
      });

      setBlurredBlocks(blocks);
    } else {
      setBlurredBlocks([]);
    }
  }, [user?.roles, safePeriods, workingDays]);


  const isBlurred = (id) => user?.roles === "trial" && blurredBlocks.includes(id);
  console.log(blurredBlocks, "blur blocks")

  const getPeriodTypeVariant = (periodType) => {
    switch (periodType) {
      case 'break': return 'success';
      case 'assembly': return 'warning';
      case 'period': return 'primary';
      default: return 'secondary';
    }
  };

  // Modified checkTeacherAvailability
  const checkTeacherAvailability = (teacherId, day, period_id, currentSectionId) => {
    // If teacherId is null, undefined, or 0 (for 'Free Period'), no conflict check is needed.
    if (!teacherId || teacherId === 0) {
      return { isAvailable: true };
    }

    const teacher = teachers.find(t => t.teacher_id === teacherId);
    if (!teacher) {
      // If teacher data is not found, we can't perform max_periods_per_day validation.
      // Depending on your application's requirements, you might want to return false or true here.
      // For now, let's assume availability if teacher data is missing (or you might want to throw an error).
      console.warn(`Teacher with ID ${teacherId} not found in teachers data.`);
      return { isAvailable: true };
    }

    const maxPeriodsPerDay = teacher.max_periods_per_day > 6 ? 6 : teacher.max_periods_per_day;
    let periodsAssignedToday = 0;

    // Count periods for the teacher in the current section's local timetable for the given day
    localPeriodsData.forEach(slot => {
      if (slot.day === day && slot.teacher_id === teacherId && slot.period_id !== period_id) {
        periodsAssignedToday++;
      }
    });

    // Count periods for the teacher in other sections' timetables for the given day
    for (const timetableObj of allTimetablesData) {
      // Skip the current section's timetable, as we've already counted it locally
      if (timetableObj.section_id === currentSectionId) {
        continue;
      }

      timetableObj.timetable.forEach(slot => {
        if (slot.day === day && slot.teacher_id === teacherId) {
          periodsAssignedToday++;
        }
      });
    }

    // Check if adding the new period exceeds max_periods_per_day
    if (periodsAssignedToday >= maxPeriodsPerDay) {
      return {
        isAvailable: false,
        reason: 'max_periods_exceeded',
        teacherName: teacher.name,
        day: day,
        maxPeriods: maxPeriodsPerDay,
      };
    }


    // Existing check for simultaneous conflict in other sections
    for (const timetableObj of allTimetablesData) {
      // Skip the current section's timetable, as we are already modifying it locally
      if (timetableObj.section_id === currentSectionId) {
        continue;
      }

      const conflictingSlot = timetableObj.timetable.find(
        (slot) => slot.day === day && slot.period_id === period_id && slot.teacher_id === teacherId
      );

      if (conflictingSlot) {
        return {
          isAvailable: false,
          reason: 'simultaneous_conflict',
          conflictingSectionName: timetableObj.section_name,
          conflictingPeriodName: conflictingSlot.period_name,
          conflictingDay: conflictingSlot.day,
          teacherName: conflictingSlot.teacher_name,
        };
      }
    }
    return { isAvailable: true };
  };


  const handleSwap = (fromId, toId) => {
    const [fromDay, fromPeriodIdStr] = fromId.split('|');
    const fromPeriodId = parseInt(fromPeriodIdStr);
    const [toDay, toPeriodIdStr] = toId.split('|');
    const toPeriodId = parseInt(toPeriodIdStr);

    setLocalPeriodsData(prevLocalPeriodsData => {
      const newLocalPeriodsData = [...prevLocalPeriodsData];

      let fromSlotContent = newLocalPeriodsData.find(
        (item) => item.day === fromDay && item.period_id === fromPeriodId
      );
      let toSlotContent = newLocalPeriodsData.find(
        (item) => item.day === toDay && item.period_id === toPeriodId
      );

      // Scenario 1: Swapping two existing slots
      if (fromSlotContent && toSlotContent) {
        // Validate before swapping
        // Only validate if the 'from' slot being moved has a teacher assigned (not a free period)
        if (fromSlotContent.teacher_id !== null && fromSlotContent.teacher_id !== undefined && fromSlotContent.teacher_id !== 0) {
          const availabilityCheckForFromTeacher = checkTeacherAvailability(fromSlotContent.teacher_id, toDay, toPeriodId, sectionId);
          if (!availabilityCheckForFromTeacher.isAvailable) {
            if (availabilityCheckForFromTeacher.reason === 'max_periods_exceeded') {
              setValidationError(
                `Cannot move ${fromSlotContent.subject_name} (Teacher: ${availabilityCheckForFromTeacher.teacherName}) to ${toDay}, ${periods.find(p => p.period_id === toPeriodId)?.period_name}. ` +
                `This teacher is already assigned **${availabilityCheckForFromTeacher.maxPeriods} periods** on ${toDay}, which is their daily maximum periods per day`
              );
            } else {
              setValidationError(
                `Cannot move ${fromSlotContent.subject_name} (Teacher: ${fromSlotContent.teacher_name}) to ${toDay}, ${availabilityCheckForFromTeacher.conflictingPeriodName}. ` +
                `This teacher is already assigned max ${availabilityCheckForFromTeacher.conflictingSectionName} in the same slot.`
              );
            }
            return prevLocalPeriodsData; // Prevent swap
          }
        }
        // Only validate if the 'to' slot being moved has a teacher assigned (not a free period)
        if (toSlotContent.teacher_id !== null && toSlotContent.teacher_id !== undefined && toSlotContent.teacher_id !== 0) {
          const availabilityCheckForToTeacher = checkTeacherAvailability(toSlotContent.teacher_id, fromDay, fromPeriodId, sectionId);
          if (!availabilityCheckForToTeacher.isAvailable) {
            if (availabilityCheckForToTeacher.reason === 'max_periods_exceeded') {
              setValidationError(
                `Cannot swap ${toSlotContent.subject_name} (Teacher: ${availabilityCheckForToTeacher.teacherName}) with ${fromDay}, ${periods.find(p => p.period_id === fromPeriodId)?.period_name}. ` +
                `This teacher is already assigned for ${availabilityCheckForToTeacher.maxPeriods} periods on ${fromDay}, which is their daily maximum periods per day.`
              );
            } else {
              setValidationError(
                `Cannot swap ${toSlotContent.subject_name} (Teacher: ${toSlotContent.teacher_name}) with ${fromDay}, ${availabilityCheckForToTeacher.conflictingPeriodName}. ` +
                `This teacher is already assigned to ${availabilityCheckForToTeacher.conflictingSectionName} in the same slot.`
              );
            }
            return prevLocalPeriodsData; // Prevent swap
          }
        }

        // Perform swap if valid
        const tempFromSlot = { ...fromSlotContent };
        const tempToSlot = { ...toSlotContent };

        const fromActualIndex = newLocalPeriodsData.indexOf(fromSlotContent);
        const toActualIndex = newLocalPeriodsData.indexOf(toSlotContent);

        if (fromActualIndex !== -1 && toActualIndex !== -1) {
          newLocalPeriodsData[fromActualIndex] = {
            ...tempToSlot,
            day: fromDay,
            period_id: fromPeriodId,
            section_id: tempToSlot.section_id || sectionId,
          };
          newLocalPeriodsData[toActualIndex] = {
            ...tempFromSlot,
            day: toDay,
            period_id: toPeriodId,
            section_id: tempFromSlot.section_id || sectionId,
          };
        }
      }
      // Scenario 2: Moving from an existing slot to an empty slot
      else if (fromSlotContent && !toSlotContent) {
        // Only validate if the 'from' slot being moved has a teacher assigned (not a free period)
        if (fromSlotContent.teacher_id !== null && fromSlotContent.teacher_id !== undefined && fromSlotContent.teacher_id !== 0) {
          const availabilityCheck = checkTeacherAvailability(fromSlotContent.teacher_id, toDay, toPeriodId, sectionId);
          if (!availabilityCheck.isAvailable) {
            if (availabilityCheck.reason === 'max_periods_exceeded') {
              setValidationError(
                `Cannot move ${fromSlotContent.subject_name} (Teacher: ${availabilityCheck.teacherName}) to ${toDay}, ${periods.find(p => p.period_id === toPeriodId)?.period_name}. ` +
                `This teacher is already assigned for ${availabilityCheck.maxPeriods} periods on ${toDay}.`
              );
            } else {
              setValidationError(
                `Cannot move ${fromSlotContent.subject_name} (Teacher: ${fromSlotContent.teacher_name}) to ${toDay}, ${availabilityCheck.conflictingPeriodName}. ` +
                `This teacher is already assigned to ${availabilityCheck.conflictingSectionName} in the same slot.`
              );
            }
            return prevLocalPeriodsData; // Prevent move
          }
        }

        const filteredData = newLocalPeriodsData.filter(
          (item) => !(item.day === fromDay && item.period_id === fromPeriodId)
        );

        filteredData.push({
          ...fromSlotContent,
          day: toDay,
          period_id: toPeriodId,
          section_id: sectionId,
        });
        return filteredData;
      }
      else if (!fromSlotContent && toSlotContent) {
        // This case would be for dragging an empty slot onto an existing one.
        // If the 'to' slot has a teacher and is not a free period, you might want to prevent clearing it
        // if that teacher is needed elsewhere or if the 'from' slot is truly empty.
        // For now, it logs a warning. If you want to allow "clearing" a slot by dragging an empty one onto it,
        // you would filter out the 'toSlotContent' here.
        console.warn("Attempted to drag an empty slot onto an existing slot. No action taken by default.");
        return prevLocalPeriodsData; // Keep original state
      }
      else {
        console.warn("Neither 'from' nor 'to' slots found. No swap performed.");
        return prevLocalPeriodsData;
      }

      setValidationError(null); // Clear any previous validation error on successful swap/move
      return newLocalPeriodsData;
    });
  };


  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setValidationError(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      setValidationError(null);
      return;
    }

    handleSwap(active.id, over.id);
  };

  const getSlot = (day, period_id) =>
    localPeriodsData.find((t) => t.day === day && t.period_id === period_id);

  const handleUpdateTimetable = async () => {
    const payload = localPeriodsData.map(item => ({
      timetable_id: item.timetable_id,
      period_id: item.period_id,
      day: item.day,
      teacher_id: item.teacher_id,
      subject_id: item.subject_id,
    }));
    console.log(payload);

    try {
      const resultAction = await dispatch(updateTimetable({ section_id: sectionId, periods: payload, school_id: schoolId, user_id: user.id }));

      if (updateTimetable.fulfilled.match(resultAction)) {
        dispatch(fetchAllTimetable());
        setEditMode(false);
        initialTimetableDataRef.current = [...localPeriodsData];
        setValidationError(null);
      } else if (updateTimetable.rejected.match(resultAction)) {
        console.error("Timetable update failed:", resultAction.error);
        setValidationError(`Timetable update failed: ${resultAction.error.message || "An unknown error occurred."}`);
      } else {
        // If for some reason it's not fulfilled/rejected but still completes, assume success
        dispatch(fetchAllTimetable());
        setEditMode(false);
        initialTimetableDataRef.current = [...localPeriodsData];
        setValidationError(null);
      }

    } catch (error) {
      console.error("Error dispatching updateTimetable:", error);
      setValidationError("Error updating timetable. Please try again.");
    }
  };


  const handleCancelEdit = () => {
    setLocalPeriodsData([...initialTimetableDataRef.current]);
    setEditMode(false);
    setValidationError(null);
  };

  const handleEditClick = () => {
    initialTimetableDataRef.current = [...localPeriodsData];
    setEditMode(true);
    setValidationError(null);
  };


  if (!timetableData || timetableData.length === 0) {
    return <Alert variant="info">No timetable data available for this section.</Alert>;
  }

  const activeSlotContent = activeId ? getSlot(activeId.split('|')[0], parseInt(activeId.split('|')[1])) : null;

  // --- Download Timetable as PDF using html2pdf.js ---
  const handleDownloadPDF = () => {
    if (!pdfRef.current) return;

    const confirm = window.confirm("Do you want to download the timetable?");
    if (confirm) {
      // Create a wrapper div
      const wrapper = document.createElement("div");

      // Add custom heading
      const heading = document.createElement("h3");
      heading.textContent = `${sectionTimetable.section_name}-Timetable`;
      heading.style.textAlign = "center";
      heading.style.marginBottom = "20px";
      wrapper.appendChild(heading);

      // Clone timetable content into wrapper
      wrapper.appendChild(pdfRef.current.cloneNode(true));

      const opt = {
        margin: [0.5, 0.5],
        filename: `Timetable_${sectionTimetable.section_name}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      };

      html2pdf().set(opt).from(wrapper).save();
    }
  };


  return (
    <Card
      key={sectionTimetable.section_id}
      className="mt-1 mb-2"
      ref={(el) =>
        (sectionRefs.current[sectionTimetable.section_id] = el)
      }
      data-section-id={sectionTimetable.section_id}
    >
      <Card.Header
        style={{
          background:
            "linear-gradient(to right, #7B2FF7 50%, #F40076 100%)",
          fontSize: "0.85rem",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          color: "white",
        }}
        className='p-1 px-3'
      >
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Timetable for {sectionTimetable.section_name}
          </h5>

          <div className="d-flex justify-content-end mb-0">
            {editMode ? (
              <>
                <Button variant="success" onClick={handleUpdateTimetable} className="me-2">
                  Update Timetable
                </Button>
                <Button variant="secondary" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {!user.roles == "trial" && <Button className="me-2" variant="primary" onClick={() => setShowPrintModal(true)}>
                  <Printer size={18} />
                  {/* Print */}
                </Button>}

                {/* Modal for printable timetable */}
                {showPrintModal && (
                  <Modal
                    show={showPrintModal}
                    onHide={() => setShowPrintModal(false)}
                    size="xl"
                    centered
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Printable Timetable</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <div>
                        <div className="d-flex mb-3 justify-content-between">
                          <ReactToPrint
                            documentTitle={`Timetable_${sectionTimetable.section_name}`}

                            pageStyle={`
          @media print {
            @page {
              size: A4;
              margin: 30px 30px !important;
            }
            body {
              -webkit-print-color-adjust: exact;
              margin: 0;
              padding: 0;
              font-size: 12px;
              line-height: 1.3;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              font-size: 11px;
              word-wrap: break-word;
            }
            th, td {
              border: 1px solid #000;
              padding: 3px;
              text-align: center;
            }
          }
        `}
                            trigger={() => (
                              <button className="no_print btn btn-outline-primary mb-3">
                                <i className="fa fa-print" aria-hidden="true"></i> Print
                              </button>
                            )}
                            onBeforeGetContent={() => {
                              const extraContent = document.createElement("div");
                              extraContent.innerHTML = `<h3 style="text-align:center;">${sectionTimetable.section_name} Timetable</h3>`;
                              printRef.current?.prepend(extraContent);
                            }}
                            content={() => printRef.current}
                          />
                          {/* <button
                            className="btn btn-danger"
                            onClick={() => setShowPrintModal(false)}
                          >
                            Close
                          </button> */}
                        </div>

                        {/* ✅ Printable content */}
                        <div ref={printRef}>
                          <div>
                            <Table bordered responsive className="my-1">
                              <thead className="table-secondary">
                                <tr>
                                  <th>Day / Period</th>
                                  {sortedPeriods.map((period) => (
                                    <th key={period.period_id}>
                                      <div className="d-flex flex-column align-items-center">
                                        <Badge bg={getPeriodTypeVariant(period.period_type)} className="mb-1">
                                          {period.period_name}
                                        </Badge>
                                        <div className="text-muted small">
                                          {period.start_time?.slice(0, 5)} - {period.end_time?.slice(0, 5)}
                                        </div>
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {days.map((day) => (
                                  <tr key={day}>
                                    <td className="fw-semibold align-middle">{day}</td>
                                    {sortedPeriods.map((period) => {
                                      const slot = localPeriodsData.find(
                                        (t) => t.day === day && t.period_id === period.period_id
                                      );
                                      if (period.period_type !== 'period') {
                                        return (
                                          <td key={`${day}|${period.period_id}`} className="text-center align-middle table-secondary">
                                            {period.period_name}
                                          </td>
                                        );
                                      }
                                      return (
                                        <td key={`${day}|${period.period_id}`} className="text-center align-middle">
                                          <div className="fw-semibold">{slot?.subject_name || "Free"}</div>
                                          <div className="text-muted small">{slot?.teacher_name}</div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    </Modal.Body>
                  </Modal>
                )}

                {!user.roles == "trial" && <Button className="me-2" variant="primary" onClick={handleDownloadPDF} >
                  <Download size={18} />
                </Button>}

                <Button variant="primary" onClick={handleEditClick}>
                  Edit Timetable
                </Button>
              </>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className='p-0'>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">
              Loading timetable for {sectionTimetable.class_name} -{" "}
              {sectionTimetable.section_name}...
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }} ref={printRef}>
            <div ref={pdfRef}>

              {validationError && (
                <Alert variant="danger" className="m-1">
                  {validationError}
                </Alert>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <Table bordered responsive className="my-1">
                  <thead className="table-secondary">
                    <tr>
                      <th>Day / Period</th>
                      {sortedPeriods.map((period) => (
                        <th key={period.period_id}>
                          <div className="d-flex flex-column align-items-center">
                            <Badge bg={getPeriodTypeVariant(period.period_type)} className="mb-1">
                              {period.period_name}
                            </Badge>
                            <div className="text-muted small">
                              {period.start_time?.slice(0, 5)} - {period.end_time?.slice(0, 5)}
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody >
                    {days.map((day) => (
                      <tr key={day} >
                        <td className="fw-semibold align-middle">{day}</td>
                        {sortedPeriods.map((period) => {
                          const id = `${day}|${period.period_id}`;
                          const slot = getSlot(day, period.period_id);

                          if (period.period_type !== 'period') {
                            return (
                              <td

                                key={id}
                                className={`table-${getPeriodTypeVariant(period.period_type)} text-center align-middle`}
                              >
                                <div className="fw-semibold">{period.period_name}</div>
                              </td>
                            );
                          }

                          return (
                            <Droppable key={id} id={id}>
                              {editMode ? (
                                <Draggable id={id} isDragging={activeId === id}>
                                  <div
                                    style={
                                      isBlurred(id)
                                        ? {
                                          filter: "blur(4px)",
                                          userSelect: "none",
                                          pointerEvents: "none",
                                          backgroundColor: 'lightgrey', // gray bg
                                          // padding: "10px",             // add spacing inside
                                          // borderRadius: "6px"         // rounded corners (optional)
                                          // padding:"10px",
                                        }

                                        : {}
                                    }
                                  >
                                    <div className="fw-semibold">
                                      {slot?.subject_name || "Free"}
                                    </div>
                                    <div className="text-muted small">
                                      {slot?.teacher_name}
                                    </div>
                                  </div>
                                </Draggable>
                              ) : (
                                <div
                                  style={
                                    isBlurred(id)
                                      ? {
                                        filter: "blur(4px)",
                                        userSelect: "none",
                                        pointerEvents: "none",
                                        backgroundColor: 'lightgrey', // gray bg
                                        // padding: "10px",
                                        // borderRadius: "6px"
                                        // padding:"10px",
                                        // borderRadius:"20px"

                                      }
                                      : {}
                                  }
                                >
                                  <div className="fw-semibold">
                                    {slot?.subject_name || "Free"}
                                  </div>
                                  <div className="text-muted small">
                                    {slot?.teacher_name}
                                  </div>
                                </div>
                              )}
                            </Droppable>



                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <DragOverlay>
                  {activeId && activeSlotContent && (
                    <Card style={{ width: '150px', padding: '8px', border: '1px solid #ccc', backgroundColor: 'white', opacity: 0.8 }}>
                      <div className="fw-semibold">{activeSlotContent.subject_name || 'Free'}</div>
                      <div className="text-muted small">{activeSlotContent.teacher_name}</div>
                    </Card>
                  )}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TimeTableView;